// src/components/Shop.js
import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Divider,
} from "@mui/material";
import { Close, Add, Remove, Delete } from "@mui/icons-material";
import { WhatsApp } from "@mui/icons-material";
import { AppContext } from "../context/AppContext";

const slugify = (txt) =>
  (typeof txt === "string" ? txt : txt?.name || "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-");

const Shop = ({ isOpen, toggleDrawer }) => {
  const { cartItems, setCartItems } = useContext(AppContext);
  const navigate = useNavigate();

  const handleQuantityChange = (idx, delta) => {
    const upd = [...cartItems];
    upd[idx].quantity = Math.max(1, upd[idx].quantity + delta);
    setCartItems(upd);
  };

  const handleRemoveItem = (idx) =>
    setCartItems(cartItems.filter((_, i) => i !== idx));

  const clearCart = () => setCartItems([]);

  const subtotal = () =>
    cartItems.reduce(
      (s, it) => s + (it.price ?? it.unitPrice ?? 0) * it.quantity,
      0
    );
  const iva = () => subtotal() * 0.15;
  const total = () => subtotal() + iva();

  const goToProduct = (item) => {
    toggleDrawer();
    if (item.variantUrl) {
      navigate(item.variantUrl);
    } else {
      const sec = slugify(item.section);
      const cat = slugify(item.category);
      const slug = item.slug || slugify(item.name);
      navigate(`/categorias/${sec}/${cat}/${slug}`);
    }
  };

  const generateWhatsAppMessage = () => {
    const baseSite = "https://hydroluz.com.ec"; 

    let msg = "Hola, estoy interesado en los siguientes productos:\n\n";

    cartItems.forEach((it) => {
      const url = it.variantUrl
        ? `${baseSite}${it.variantUrl}`
        : `${baseSite}/categorias/${slugify(it.section)}/${slugify(
            it.category
          )}/${it.slug || slugify(it.name)}`;

      const price = (it.price ?? it.unitPrice ?? 0).toFixed(2);

      msg += `• ${it.name}\n`;
      if (it.variant) {
        msg += `  – ${it.variant.type}: ${it.variant.value}\n`;
        if (it.sku) msg += `  – SKU: ${it.sku}\n`;
      }
      msg += `  – Cantidad: ${it.quantity}\n`;
      msg += `  – Precio: US$ ${price}\n`;
      msg += `  – URL: ${url}\n\n`;
    });

    msg += `Subtotal: US$ ${subtotal().toFixed(2)}\n`;
    msg += `IVA (15%): US$ ${iva().toFixed(2)}\n`;
    msg += `Total: US$ ${total().toFixed(2)}\n`;

    return `https://wa.me/593991911882?text=${encodeURIComponent(msg)}`;
  };

  return (
    <>
      {isOpen && (
        <IconButton
          onClick={toggleDrawer}
          sx={{
            position: "fixed",
            top: 16,
            right: 355,
            zIndex: 1500,
            color: "#000",
            bgcolor: "#fff",
            border: "1px solid #ddd",
            "&:hover": { bgcolor: "#f0f0f0" },
          }}
        >
          <Close />
        </IconButton>
      )}

      <Drawer anchor="right" open={isOpen} onClose={toggleDrawer}>
        <Box
          sx={{
            width: 350,
            p: 2,
            bgcolor: "#fff",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6">Carrito</Typography>
            <Button
              onClick={clearCart}
              sx={{
                textTransform: "none",
                color: "#007B83",
                border: "1px solid #007B83",
                fontSize: 14,
                bgcolor: "#fff",
                "&:hover": { bgcolor: "#007B8320" },
              }}
            >
              Limpiar
            </Button>
          </Box>

          {cartItems.length === 0 ? (
            <Typography
              variant="body1"
              sx={{ textAlign: "center", mt: 5, color: "#555" }}
            >
              Tu carrito está vacío
            </Typography>
          ) : (
            <>
              <Box sx={{ flexGrow: 1, maxHeight: "60vh", overflowY: "auto" }}>
                {cartItems.map((it, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      mb: 2,
                      borderBottom: "1px solid #ddd",
                      pb: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <img
                        src={it.image || it.images?.[0] || ""}
                        alt={it.name}
                        style={{
                          width: 80,
                          height: 80,
                          objectFit: "contain",
                          borderRadius: 5,
                        }}
                      />
                      <Box sx={{ ml: 2, flex: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: "bold",
                            cursor: "pointer",
                            color: "#333",
                          }}
                          onClick={() => goToProduct(it)}
                        >
                          {it.name}
                        </Typography>
                        {it.variant && (
                          <Typography variant="body2" sx={{ color: "#666" }}>
                            {it.variant.type}: {it.variant.value}
                          </Typography>
                        )}
                        <Typography variant="body2" sx={{ color: "#888" }}>
                          REF: {it.code}
                        </Typography>
                        {it.sku && (
                          <Typography variant="body2" sx={{ color: "#888" }}>
                            SKU: {it.sku}
                          </Typography>
                        )}
                        <Typography
                          variant="body2"
                          sx={{ color: "#1565C0", fontWeight: "bold" }}
                        >
                          US$ {(it.price ?? it.unitPrice ?? 0).toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mt: 1,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <IconButton
                          size="small"
                          onClick={() => handleQuantityChange(idx, -1)}
                          sx={{ bgcolor: "#f0f0f0" }}
                        >
                          <Remove />
                        </IconButton>
                        <Typography variant="body2" sx={{ mx: 1 }}>
                          {it.quantity}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleQuantityChange(idx, 1)}
                          sx={{ bgcolor: "#f0f0f0" }}
                        >
                          <Add />
                        </IconButton>
                      </Box>
                      <IconButton
                        onClick={() => handleRemoveItem(idx)}
                        sx={{ color: "#e53935" }}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </Box>

              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography>Subtotal</Typography>
                <Typography>US$ {subtotal().toFixed(2)}</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography>IVA (15%)</Typography>
                <Typography>US$ {iva().toFixed(2)}</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                <Typography fontWeight="bold">Total</Typography>
                <Typography fontWeight="bold">US$ {total().toFixed(2)}</Typography>
              </Box>

              <Button
                variant="contained"
                fullWidth
                startIcon={<WhatsApp />}
                href={generateWhatsAppMessage()}
                target="_blank"
                sx={{
                  bgcolor: "#25D366",
                  color: "#fff",
                  "&:hover": { bgcolor: "#1ebe5d" },
                }}
              >
                Comprar
              </Button>

              <Box sx={{ mt: 2 }}>
                <Button
                  variant="text"
                  onClick={() => {
                    navigate("/categorias");
                    toggleDrawer();
                  }}
                  sx={{
                    color: "#1565C0",
                    textDecoration: "underline",
                    fontWeight: "bold",
                    width: "100%",
                  }}
                >
                  Seguir Comprando
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default Shop;
